import { PropsWithChildren } from 'react';

import { useSwingMeasuredParameters } from '@hooks';
import { useFloorSettings, useReadOnlyCurrentActivity, useUserSettings } from '@queries';
import { LoadingScreen } from '@screens';
import {
    activeUserSettingsAtom,
    currentActivityAtom,
    floorSettingsAtom,
    swingMeasuredParametersAtom,
    userLocaleAtom,
    userSettingsAtom,
    useSyncAtom,
    withUserScope,
} from '@store';

/**
 * DO NOT CONFUSE THIS WITH USER AUTH PROVIDER.
 * This component exists only to load user related/scoped data, and ensure that it is loader.
 */
const UserGuard = ({ children }: PropsWithChildren) => {
    const userSettingsRequest = useUserSettings();
    useSyncAtom(userSettingsAtom, userSettingsRequest.data);
    useSyncAtom(activeUserSettingsAtom, userSettingsRequest.data);
    useSyncAtom(userLocaleAtom, userSettingsRequest.data?.locale);

    const swingMeasuredParametersRequest = useSwingMeasuredParameters();
    useSyncAtom(swingMeasuredParametersAtom, swingMeasuredParametersRequest.data);

    /**
     *  Will be added as User scoped entities occur.
     *
     *  Right now, context of a lot fo atoms depend on userId, which is determined by activity user id (not the logged in user).
     *  That means we want to switch, settings, goals, layouts, etc. Per activity.
     *
     *  It might be useful to load logged in user's data here, and then set update it in ActivityGuard when user ids match.
     *
     *  For now, this component only exists to provide user scope.
     */
    // This hook is inactive if @auth-v2 booth provider does not exist.
    const currentActivity = useReadOnlyCurrentActivity();
    useSyncAtom(currentActivityAtom, currentActivity.data, true);

    const floorSettingResponse = useFloorSettings();
    useSyncAtom(floorSettingsAtom, floorSettingResponse.data);

    if (floorSettingResponse.isLoading || !floorSettingResponse.data || !swingMeasuredParametersRequest.data) {
        return <LoadingScreen message="Loading user settings..." />;
    }

    return children;
};

export const UserLoader = withUserScope(UserGuard);
